import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, fetchConfig, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppHeader, PageShell } from "@/components/AppHeader";
import { LineupCard } from "@/components/lineups/LineupCard";
import { LineupEditor } from "@/components/lineups/LineupEditor";

export default function LineupsPage() {
  const { teamId } = useParams();
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [lineups, setLineups] = useState(null);
  const [editing, setEditing] = useState(null);
  const [config, setConfig] = useState(null);

  const load = () => api.lineups(teamId).then(setLineups);
  useEffect(() => { fetchConfig().then(setConfig); api.team(teamId).then(setTeam); load(); }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (body) => {
    try {
      if (editing.id) await api.updateLineup(teamId, editing.id, body);
      else await api.createLineup(teamId, body);
      toast.success("Lineup saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const remove = (l) => api.deleteLineup(teamId, l.id).then(() => { toast.success("Lineup deleted"); load(); }, (e) => toast.error(formatApiError(e)));

  if (!team || !lineups || !config) return <div className="min-h-screen grid place-items-center font-heading text-2xl">Loading lineups...</div>;
  const canEdit = user.access.has_access;
  return (
    <PageShell>
      <AppHeader><Link to={`/coach/teams/${teamId}`} className="nav-btn" data-testid="lineups-back-link"><ArrowLeft size={18} /> Playbook</Link></AppHeader>
      <div className="team-banner">
        <div className="min-w-0">
          <p className="ctl-label">Lineups · shared with your coaches only</p>
          <h1 className="font-heading text-2xl sm:text-4xl font-extrabold uppercase tracking-tight truncate">{team.name}</h1>
        </div>
        {canEdit && <button className="btn-primary" data-testid="new-lineup-button" onClick={() => setEditing({})}><Plus size={18} /> New lineup</button>}
      </div>
      {lineups.length === 0 && <div className="card text-center !py-10 font-semibold" data-testid="lineups-empty">No lineups yet. Build your batting order and positions for the next game.</div>}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="lineup-list">
        {lineups.map((l) => (
          <LineupCard key={l.id} lineup={l} cfg={config.sports[team.sport][team.level]} teamName={team.name} onEdit={canEdit ? () => setEditing(l) : undefined} onDelete={() => remove(l)} />
        ))}
      </div>
      {editing && <LineupEditor initial={editing.id ? editing : null} onClose={() => setEditing(null)} onSave={save} />}
    </PageShell>
  );
}
